import { ArrowTopRightOnSquareIcon, ArrowUturnLeftIcon, ChevronRightIcon, XMarkIcon } from "@heroicons/react/24/outline";
import React from "react";

const ComplaintsProcess: React.FC = () => {
  const linkItems = [
    { text: "Our Complaints Process", icon: ChevronRightIcon, target: "our-complaints-process" },
    { text: "How to Contact Us", icon: ChevronRightIcon, target: "how-to-contact-us" },
    { text: "What We Need", icon: ChevronRightIcon, target: "what-we-need" },
    { text: "What to Expect Next", icon: ChevronRightIcon, target: "what-to-expect-next" },
    { text: "What if you're not happy with our response?", icon: ChevronRightIcon, target: "what-if-youre-not-happy-with-our-response" },
  ];

  return (
    <div className="flex flex-col items-center container pt-20">
      {/* Header Section */}
      <header className="w-full px-5">
        <h1 className="text-4xl font-extrabold text-gray-900">How to Complain</h1>
        <p className="mt-4 text-lg text-gray-700">This Faq was last updated on 8th July 2025.  </p>
      </header>

      {/* Main Content Section */}
      <section className="w-full px-5 mt-8 p-6">
        <p className="text-gray-800">We aim to provide you with the highest standards of service. However, there may be occasions when our service does not meet your expectations but telling us about it gives us a chance to fix things.</p>
      </section>

      {/* Link Section */}
      <div className="w-full mt-8 bg-gray-100 rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {linkItems.map((item, index) => {
            const IconComponent = item.icon; // Get the icon component dynamically
            return (
              <a
                key={index}
                href={`#${item.target}`} // Anchor link to the section
                className="flex items-center justify-between bg-white p-4 rounded-lg shadow-md hover:bg-gray-50 transition"
              >
                <span className="text-gray-900 font-medium">{item.text}</span>
                <IconComponent className="h-6 w-6" /> {/* Render the icon dynamically */}
              </a>
            );
          })}
        </div>
      </div>

      <section id="our-complaints-process" className="w-full px-5 my-8 scroll-mt-20">
        <h3 className="text-2xl my-4">Our Complaints Process</h3>
        <p className="text-gray-800 mb-4">We want to:</p>
        <ul className="list-disc ml-6 text-gray-800 mb-4">
          <li>Make it easy for you to raise your complaint</li>
          <li>Listen to your complaint</li>
          <li>Consider how you would like us to remedy your complaint</li>
          <li>Make sure you are satisfied with how your complaint was handled</li>
          <li>Ensure lessons are learned, if possible, to minimise the chance of a complaint re-occurring</li>
        </ul>
      </section>
      <section id="how-to-contact-us" className="w-full px-5 my-8 scroll-mt-20">
        <h4 className="text-xl font-semibold mt-6 mb-2">How to Contact Us</h4>
        <p className="text-gray-800 mb-2">
          By email:{" "}
          <a href="mailto:info@orenda.finance" className="text-blue-600 underline">
            info@orenda.finance
          </a>
        </p>
      </section>
      <section id="what-we-need" className="w-full px-5 my-8 scroll-mt-20">
        <h4 className="text-xl font-semibold mt-6 mb-2">What We Need</h4>
        <p className="text-gray-800 mb-2">Please provide us with as much information as possible when making your complaint. This will help us to understand the issue and resolve it quickly. Please include:</p>
        <ul className="list-disc ml-6 text-gray-800 mb-4">
          <li>Your name and address</li>
          <li>Your account details</li>
          <li>A description of your complaint and how it’s affected you</li>
          <li>When the issue happened</li>
          <li>Your contact details and how you would like us to contact you</li>
        </ul>
      </section>
      <section id="what-to-expect-next" className="w-full px-5 my-8 scroll-mt-20">
        <h4 className="text-xl font-semibold mt-6 mb-2">What to Expect Next</h4>
        <ul className="list-disc ml-6 text-gray-800 mb-4">
          <li>
            <span className="font-semibold">Acknowledgment:</span> Our aim is to resolve your complaint as quickly as possible but you will receive a response from us within 2 business days so you know we have received your complaint. This will
            contain your complaint reference number for your records and will help us find your information quickly should you need to contact us.
          </li>
          <li>
            <span className="font-semibold">15 business days:</span> In the majority of cases, we will be able to resolve your complaint within 15 business days. If we have not resolved it within 15 business days, we will contact you to update you on
            the progress and tell you how much longer we anticipate it will take.
          </li>
          <li>
            <span className="font-semibold">Up to 35 business days:</span> We will continue to keep you informed in writing and let you know when you should expect to hear from us. <br />
            Although we have up to 35 business days, we will send you our final response as soon as we complete the investigation into your complaint. <br />
            In the unlikely event we have not been able to finalise our investigation by the end of 35 business days, we will send you a final response communication and advise what next steps you can take.
          </li>
        </ul>
      </section>
      <section id="what-if-youre-not-happy-with-our-response" className="w-full px-5 my-8 scroll-mt-20">
        <h4 className="text-xl font-semibold mt-6 mb-2">What if you're not happy with our response?</h4>
        <p className="text-gray-800 mb-2">
          You can refer the problem to the issuer of your account or card (TransactPay). <br />
          If you don’t agree with our response and you wish to take it further, in the first instance you should contact the TransactPay’s Complaints Department by writing to{" "}
          <a href="mailto:complaints@transactpay.com" className="text-blue-600 underline">
            complaints@transactpay.com
          </a>{" "}
          for further assistance.
        </p>
        <p className="text-gray-800 mb-2">
          In the unlikely event that TransactPay are unable to resolve your issue to your satisfaction you have the right to refer your complaint to the Gibraltar Financial Services Commission at the following address:
        </p>
        <ul className="list-none ml-0 text-gray-800 mb-2">
          <li>Payment Service Team, Gibraltar Financial Services Commission, PO Box 940, Suite 3, Ground Floor, Atlantic Suites, Europort Avenue, Gibraltar or by email at complaints@gfsc.gi.</li>
        </ul>
        <p className="text-gray-800">If you have not contacted TransactPay, the Gibraltar Financial Services Commission will ask you to contact them in the first instance, to give TransactPay a chance to put things right.</p>
      </section>
    </div>
  );
};

export default ComplaintsProcess;
